// src/pages/ApplicationTeams/index.tsx
import { useCallback, useState } from 'react';
import { Text, Flex, useToast, Button } from '@chakra-ui/react';
import { PageContent } from 'src/components/PageContent';
import { SectionBox } from 'src/components/SectionBox';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { breadcrumb } from './breadcrumb';
import { GetParamsObject } from '@/model/query-params';
import { useFiltersForm } from '@/utils/useFiltersForm';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import { Club } from '@/model';
import { TeamsFilters } from './types';
import { useClub, useDeleteClubMutation, useGetClubById } from '@/services/queries/club';
import { TeamsTable } from './components/TeamsTable/TeamsTable';
import { TeamsFilterBox } from './components/TeamsFilterBox';
import { TeamsEditModal } from './components/TeamsEditModal';
import { TeamsCreateModal } from './components/TeamsCreateModal';
import { useTranslation } from 'react-i18next';

export const ApplicationTeams = () => {
  const { t } = useTranslation();
  const toast = useToast();

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const [selectedItem, setSelectedItem] = useState<Club | undefined>(undefined);

  const deleteMutation = useDeleteClubMutation();
  const getItemByUUID = useGetClubById();

  const filtersForm = useFiltersForm<TeamsFilters>({
    defaultValues: {
      name: '',
      start_date: '',
      end_date: '',
    },
  });
  const filterData = filtersForm.watch();

  const [sorting, setSorting] = useState<{ field: string; desc: boolean }>({ field: 'created_at', desc: true });
  const [pagination, setPagination] = useState<{ pageNumber: number; pageSize: number }>({
    pageNumber: 1,
    pageSize: 10,
  });

  const params = GetParamsObject(
    filterData,
    pagination.pageNumber,
    pagination.pageSize,
    sorting.desc ? 'desc' : 'asc',
    sorting.field,
  );

  const { data: itemData, isLoading, forceRefetch } = useClub(params);

  const itemsData = itemData?.data || [];

  const onHandleDelete = (item: Club) => {
    setSelectedItem(item);
    setIsConfirmModalOpen(true);
  };

  const onHandleDeleteConfirm = () => {
    if (!selectedItem?.uuid) return;

    setIsConfirmModalOpen(false);

    deleteMutation.mutate(selectedItem.uuid, {
      onSuccess: () => {
        toast({
          title: t('pages.application_teams.application_teams_delete_success'),
          status: 'success',
        });
        forceRefetch();
        setSelectedItem(undefined);
      },
      onError: (resp: any) => {
        setSelectedItem(undefined);
        toast({
          status: 'error',
          title: (resp && resp.message) || t('pages.application_teams.application_teams_delete_error'),
        });
      },
    });
  };

  const onHandleEdit = (uuid: string) => {
    getItemByUUID.mutate(uuid, {
      onSuccess: res => {
        setSelectedItem(res);
        setIsEditModalOpen(true);
      },
      onError: (resp: any) => {
        toast({
          status: 'error',
          title: (resp && resp.message) || t('pages.application_teams.application_teams_not_found'),
        });
      },
    });
  };

  const onHandleEditDone = useCallback(() => {
    setIsEditModalOpen(false);
    setSelectedItem(undefined);
    forceRefetch();
  }, [forceRefetch]);

  const onHandleCreateDone = useCallback(() => {
    setIsCreateModalOpen(false);
    forceRefetch();
  }, [forceRefetch, setIsCreateModalOpen]);

  return (
    <PageContent title={t('pages.application_teams.application_teams_page_title')} breadcrumbItems={breadcrumb}>
      <SectionBox bodyProps={{ gap: 'small' }}>
        <Flex direction={{ base: 'column', md: 'row' }} justifyContent={{ md: 'space-between' }} gap={4}>
          <Flex alignItems='center' gap={4}>
            <Text fontWeight='bold' color='brand.500'>
              {t('pages.application_teams.application_teams_label')}
            </Text>
          </Flex>

          <Button
            display='flex'
            alignItems='center'
            gap={2}
            onClick={() => setIsCreateModalOpen(true)}
            width={{ base: '100%', md: 'initial' }}
          >
            <ChakraFeatherIcon name='plus-circle' />
            {t('pages.application_teams.application_teams_create_button')}
          </Button>
        </Flex>
        <SectionBox bodyProps={{ gap: 'small' }}>
          <TeamsFilterBox filtersForm={filtersForm} forceRefetch={forceRefetch} />
        </SectionBox>

        <Flex direction='column' gap='deka' mt={8}>
          <TeamsTable
            data={itemsData}
            isLoading={isLoading}
            sorting={sorting}
            pageNumber={pagination.pageNumber}
            pageSize={pagination.pageSize}
            totalPages={itemData?.meta.pages || 0}
            totalItems={itemData?.meta.total || 0}
            onSortingChange={(field, desc) => setSorting({ field, desc })}
            onPaginationChange={(pageNumber, pageSize) => setPagination({ pageNumber, pageSize })}
            onEditClick={item => onHandleEdit(item.uuid)}
            onDeleteClick={item => onHandleDelete(item)}
          />
        </Flex>
      </SectionBox>

      {selectedItem && (
        <TeamsEditModal
          isOpen={!!isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedItem(undefined);
          }}
          item={selectedItem}
          onDone={onHandleEditDone}
          itemsData={itemsData} // Adicionado para validação
        />
      )}

      <TeamsCreateModal
        isOpen={!!isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onDone={onHandleCreateDone}
        itemsData={itemsData} // Adicionado para validação
      />

      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={() => {
          setIsConfirmModalOpen(false);
          setSelectedItem(undefined);
        }}
        onConfirm={onHandleDeleteConfirm}
        isLoading={false}
        title={t('pages.application_teams.application_teams_delete_modal_title')}
        description={
          <Text>
            {t('pages.application_teams.application_teams_delete_confirm')}
            <Text as='span' fontWeight='bold'>
              "{selectedItem?.name}"
            </Text>
            ?
          </Text>
        }
      />

      <CenteredSpinner isLoading={getItemByUUID.isLoading || deleteMutation.isLoading} />
    </PageContent>
  );
};